import { raw } from '../api/http'
import { ApiError } from '../api/problem'
import type {
  RegisterGuestBody,
  RegisterGuestResponse,
  RegisterHotelGuestBody,
  TenantIzvor,
  TokenPair,
} from '../api/types'
import { zaboraviResort, zapamtiResort } from '../gost/tenant'
import { stvoriSesiju, type SessionStore } from './store'

/**
 * Sesija GOSTA. Ista izvedba kao za osoblje, drugi ključ u pohrani — gost i
 * osoblje se u istom pregledniku ne gaze (zadatak 7 §2, vidi `store.ts`).
 *
 * Prijava je passwordless i dvokoračna (API §1): `/auth/login` šalje SMS kod i
 * uvijek vraća 204 (inače bi endpoint bio popis gostiju resorta), a `/auth/verify`
 * ga mijenja za tokene. Sesija traje boravak: refresh se odbija čim guest_access
 * nije aktivan, pa se pristup gasi sam, bez crne liste tokena (CLAUDE.md §8).
 */

const KLJUC_REFRESH = 'shuttly.gost.refresh_token'

export const gostSesija: SessionStore = stvoriSesiju(KLJUC_REFRESH)

/**
 * Svaki `/auth/*` poziv je usput i provjera resorta: prođe li, resort postoji i
 * vrijedi ga zapamtiti; vrati li `tenant_not_found`, ne postoji i zapamćeni se
 * briše. Bez tog drugog dijela bi jednom krivo upisana subdomena zauvijek
 * ostala "zatečeni resort" i gost bi zurio u istu grešku (zadatak 10 §5).
 */
async function uzResort<T>(izvor: TenantIzvor, poziv: () => Promise<T>): Promise<T> {
  try {
    const rezultat = await poziv()
    zapamtiResort(izvor)

    return rezultat
  } catch (kvar) {
    if (kvar instanceof ApiError && kvar.key === 'tenant_not_found') zaboraviResort()
    throw kvar
  }
}

/** `POST /auth/register` — samoposlužna registracija; recepcija je potvrđuje. */
export function registrirajGosta(
  izvor: TenantIzvor,
  podaci: RegisterGuestBody,
): Promise<RegisterGuestResponse> {
  return uzResort(izvor, () =>
    raw<RegisterGuestResponse>('/auth/register', {
      metoda: 'POST',
      tijelo: { ...izvor, ...podaci },
    }),
  )
}

/**
 * `POST /auth/register/hotel` — registracija gosta hotela s registracijskog QR-a.
 *
 * Ne prolazi kroz `uzResort`: tenant ovdje ne ide IZ klijenta nego iz tokena
 * letka. Zato subdomenu vraća POSLUŽITELJ, a pamti se tek onda — to je i dalje
 * "zadnji potvrđen resort" (zadatak 10 §5), samo potvrđen odgovorom umjesto
 * prolaskom. Bez toga bi gost prijavu kodom otvarao upisivanjem subdomene rukom.
 */
export async function registrirajGostaHotela(
  podaci: RegisterHotelGuestBody,
): Promise<RegisterGuestResponse> {
  const odgovor = await raw<RegisterGuestResponse>('/auth/register/hotel', {
    metoda: 'POST',
    tijelo: podaci,
  })

  if (odgovor.subdomena) zapamtiResort({ subdomena: odgovor.subdomena })

  return odgovor
}

/**
 * `POST /auth/magic` — veza iz dobrodošlice u zamjenu za sesiju.
 *
 * Tenant nosi sam token u prefiksu, pa zahtjev ne šalje ništa drugo: ni broj ni
 * subdomenu. Otvaranje vezu NE troši — višekratna je i s bilo kojeg uređaja.
 *
 * **VEZA POSTAVLJA JEZIK APLIKACIJE** (D14-B §1). Gost je poruku dobio na jeziku
 * koji je pult upisao na najavu; sučelje koje se iz nje otvori mora govoriti
 * isto. Dotad je gost iz njemačkog maila ulazio u hrvatsko sučelje i sam tražio
 * gdje se jezik mijenja — na jeziku koji ne čita.
 *
 * **Oznaka se VRAĆA, a ne primjenjuje ovdje.** Ovaj modul je sloj sesije i zovu
 * ga i `api/client.ts` i i18n — uvoz rječnika odavde zatvorio bi krug uvoza i
 * srušio pokretanje. Primjenjuje ju EKRAN veze (`gost/pages/Ulaz.tsx`), i to
 * prije nego ijedan gostov ekran bude nacrtan.
 *
 * `null` kad poslužitelj jezik nije poslao (stariji poslužitelj, prijava kodom):
 * sučelje tada ostaje na onome što gost već ima.
 */
export async function prijaviVezom(token: string): Promise<string | null> {
  const par = await raw<TokenPair>('/auth/magic', { metoda: 'POST', tijelo: { token } })

  gostSesija.primi(par)

  return par.jezik?.trim().toLowerCase() || null
}

/**
 * `POST /auth/magic/resend` — mrtva veza traži novu (terenski nalaz 2).
 *
 * Šalje se mrtvi token i adresa boravka: token kaže na koji se boravak zahtjev
 * odnosi (i, prefiksom, u koji resort), a adresa je JEDINI dokaz — sam token je
 * upravo ono što više ne vrijedi.
 *
 * Odgovor je **uvijek 204**, i kad se adresa ne poklapa: razlika bi bila podatak
 * o tuđem boravku. Zato sučelje nakon slanja kaže isto što i poslužitelj zna —
 * "ako se adresa poklapa, poruka je na putu".
 */
export function zatraziNovuVezu(token: string, email: string): Promise<void> {
  return raw<void>('/auth/magic/resend', { metoda: 'POST', tijelo: { token, email } })
}

/** `POST /auth/login` — traži SMS kod. 204 i kad broj ne postoji (API §1). */
export function posaljiKod(izvor: TenantIzvor, mobitel: string): Promise<void> {
  return uzResort(izvor, () =>
    raw<void>('/auth/login', { metoda: 'POST', tijelo: { ...izvor, mobitel } }),
  )
}

/** `POST /auth/verify` — kod → par tokena; sesija gosta počinje ovdje. */
export async function potvrdiKod(
  izvor: TenantIzvor,
  mobitel: string,
  kod: string,
): Promise<void> {
  const par = await uzResort(izvor, () =>
    raw<TokenPair>('/auth/verify', {
      metoda: 'POST',
      tijelo: { ...izvor, mobitel, kod },
    }),
  )

  gostSesija.primi(par)
}

/** Samo za testove: vraća modul u polazno stanje. */
export const _resetGostSesije = (): void => gostSesija._reset()
